import React from "react";

export default function TermsContent() {
  const updated = new Date().toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const H2 = ({ id, children }) => (
    <h2 id={id} className="group text-white text-xl font-semibold tracking-tight">
      <span>{children}</span>
      <a
        href={`#${id}`}
        className="ml-2 align-middle text-slate-400 opacity-0 group-hover:opacity-100 hover:text-slate-200 transition"
        aria-label="Link to section"
      >
        #
      </a>
    </h2>
  );

  const Bullets = ({ children }) => (
    <ul className="list-disc pl-5 space-y-2 text-slate-300">{children}</ul>
  );

  return (
    <article className="mx-auto max-w-3xl text-slate-300 leading-relaxed">
      <p className="text-sm text-slate-400">Last updated: {updated}</p>

      <div className="mt-4 divide-y divide-white/10">
        {/* 1 */}
        <section className="pt-6 first:pt-0">
          <H2 id="who-we-are">1. Who we are</H2>
          <p className="mt-2">
            Hey Lets Travel UK Ltd (“HLT”, “we”, “us”, “our”) is a UK limited company
            that creates and manages lifestyle events, artist management services,
            subscriptions, and travel concierge packages. Our platform enables you to
            discover events, purchase tickets, subscribe to artist plans, and access
            additional services. Our website and app are collectively the “Service”.
          </p>
          <p className="mt-2">
            Contact:{" "}
            <a
              href="mailto:support@hey-lets-travel.com"
              className="underline decoration-white/20 hover:decoration-white text-slate-200"
            >
              support@hey-lets-travel.com
            </a>
          </p>
        </section>

        {/* 2 */}
        <section className="pt-6">
          <H2 id="acceptance">2. Acceptance of these Terms</H2>
          <p className="mt-2">
            By creating an account, purchasing a ticket or subscription, or otherwise
            using the Service, you agree to these Terms. If you do not agree, do not use
            the Service.
          </p>
        </section>

        {/* 3 */}
        <section className="pt-6">
          <H2 id="eligibility">3. Eligibility</H2>
          <Bullets>
            <li>You must be at least 18 years old to register and use the Service.</li>
            <li>You must have the legal capacity to enter into a binding contract.</li>
            <li>
              Some events may have additional entry restrictions (e.g. age limits at
              licensed venues).
            </li>
          </Bullets>
        </section>

        {/* 4 */}
        <section className="pt-6">
          <H2 id="your-account">4. Your account</H2>
          <Bullets>
            <li>You must provide accurate, up-to-date information.</li>
            <li>Accounts require email verification and secure passwords.</li>
            <li>For security, we may lock accounts after repeated failed login attempts.</li>
            <li>
              We may suspend or close accounts where fraud, resale abuse, or breach of
              terms occurs.
            </li>
          </Bullets>
        </section>

        {/* 5 */}
        <section className="pt-6">
          <H2 id="user-conduct">5. User conduct</H2>
          <p className="mt-2">You agree not to:</p>
          <Bullets>
            <li>Engage in illegal activity, harassment, impersonation, or spamming.</li>
            <li>Use bots or automation to buy tickets.</li>
            <li>Resell tickets outside our policy.</li>
            <li>Attempt to bypass or exploit our systems.</li>
          </Bullets>
        </section>

        {/* 6 */}
        <section className="pt-6">
          <H2 id="events-tickets">6. Events &amp; tickets</H2>
          <Bullets>
            <li>
              Events may be organised directly by us or by third-party promoters. Where
              we sell on behalf of a third party, your contract is with them and we act
              only as their ticketing agent.
            </li>
            <li>
              Tickets are subject to each event’s entry conditions (e.g. ID, dress code,
              age restrictions).
            </li>
            <li>
              All ticket sales are final unless otherwise stated. If an event is
              cancelled or rescheduled, refunds or transfers will follow our or the
              organiser’s policy and applicable law.
            </li>
            <li>Digital tickets (e.g. QR codes) are your responsibility to keep secure.</li>
          </Bullets>
        </section>

        {/* 7 */}
        <section className="pt-6">
          <H2 id="subscriptions-benefits">7. Subscriptions &amp; benefits</H2>
          <Bullets>
            <li>We offer subscription tiers (Entry, Middle, Headliner).</li>
            <li>Subscriptions auto-renew unless cancelled in advance.</li>
            <li>
              Benefits such as free ticket allocations reset annually and do not roll
              over unless specified.
            </li>
            <li>
              We reserve the right to adjust pricing or benefits prospectively, with
              notice where required.
            </li>
          </Bullets>
        </section>

        {/* 8 */}
        <section className="pt-6">
          <H2 id="payments-fees">8. Payments &amp; fees</H2>
          <Bullets>
            <li>Payments are processed securely via Stripe.</li>
            <li>
              We do not store your card details, only customer identifiers for recurring
              payments.
            </li>
            <li>All applicable taxes and fees are shown at checkout.</li>
            <li>
              Chargebacks, failed payments, or disputes may result in account suspension
              or ticket cancellation.
            </li>
          </Bullets>
        </section>

        {/* 9 */}
        <section className="pt-6">
          <H2 id="affiliate">9. Affiliate codes &amp; commissions</H2>
          <Bullets>
            <li>Affiliates may be issued discount/commission codes.</li>
            <li>
              Codes must not be misused (e.g. spamming, misleading advertising,
              reselling outside terms).
            </li>
            <li>
              Commission is subject to performance thresholds, fraud checks, and
              chargeback windows.
            </li>
            <li>Affiliate quotas and commission rates may be updated periodically.</li>
          </Bullets>
        </section>

        {/* 10 */}
        <section className="pt-6">
          <H2 id="artist-management">10. Artist management &amp; referrals</H2>
          <Bullets>
            <li>DJs, producers, and creators may be managed under HLT Management.</li>
            <li>
              Referral opportunities may be offered to affiliates or members, but are
              subject to vetting.
            </li>
            <li>
              Management contracts and opportunities are separate agreements outside
              these Terms.
            </li>
          </Bullets>
        </section>

        {/* 11 */}
        <section className="pt-6">
          <H2 id="travel-concierge">11. Travel concierge</H2>
          <Bullets>
            <li>
              Concierge services (e.g. accommodation, transport, VIP packages) are
              offered as an additional service.
            </li>
            <li>
              All concierge bookings are subject to availability and the terms of
              third-party providers.
            </li>
            <li>
              We act as an intermediary and are not liable for third-party performance,
              though we will manage bookings in line with consumer law.
            </li>
          </Bullets>
        </section>

        {/* 12 */}
        <section className="pt-6">
          <H2 id="ip">12. Intellectual property</H2>
          <p className="mt-2">
            All HLT branding, content, software, and event assets remain our property or
            that of our licensors. You may not copy, reproduce, or misuse these without
            written permission.
          </p>
        </section>

        {/* 13 */}
        <section className="pt-6">
          <H2 id="suspension-termination">13. Suspension &amp; termination</H2>
          <Bullets>
            <li>
              We may suspend or terminate accounts for breach of these Terms, fraud, or
              activity posing risks to our users or systems.
            </li>
            <li>
              You may close your account at any time; certain records may be retained
              for legal or regulatory purposes.
            </li>
          </Bullets>
        </section>

        {/* 14 */}
        <section className="pt-6">
          <H2 id="disclaimers">14. Disclaimers</H2>
          <p className="mt-2">
            We provide the Service “as is” and do not guarantee uninterrupted or
            error-free use. Event details are provided in good faith but may be subject
            to last-minute change.
          </p>
        </section>

        {/* 15 */}
        <section className="pt-6">
          <H2 id="limitation-of-liability">15. Limitation of liability</H2>
          <p className="mt-2">
            To the fullest extent permitted by law, we are not liable for indirect,
            incidental, or consequential damages (loss of profits, data, goodwill).
            Our total liability for any claim is capped at the amount you paid to us in
            the 12 months prior to the claim. Nothing in these Terms limits rights that
            cannot legally be excluded.
          </p>
        </section>

        {/* 16 */}
        <section className="pt-6">
          <H2 id="indemnity">16. Indemnity</H2>
          <p className="mt-2">
            You agree to indemnify and hold us harmless against claims arising from
            misuse of the Service or breach of these Terms.
          </p>
        </section>

        {/* 17 */}
        <section className="pt-6">
          <H2 id="changes">17. Changes to these Terms</H2>
          <p className="mt-2">
            We may update these Terms. The new version will be published on our
            platform. Continued use of the Service after changes constitutes acceptance.
          </p>
        </section>

        {/* 18 */}
        <section className="pt-6">
          <H2 id="governing-law">18. Governing law</H2>
          <p className="mt-2">
            These Terms are governed by the laws of England and Wales. If you are
            outside the UK, mandatory consumer rights in your local jurisdiction still
            apply.
          </p>
        </section>

        {/* 19 */}
        <section className="pt-6">
          <H2 id="contact">19. Contact</H2>
          <p className="mt-2">
            For any questions, contact us at:{" "}
            <a
              href="mailto:support@hey-lets-travel.com"
              className="underline decoration-white/20 hover:decoration-white text-slate-200"
            >
              support@hey-lets-travel.com
            </a>
            .
          </p>
        </section>
      </div>
    </article>
  );
}
